// frontend/src/pages/admin/UsersManagement/UsersManagement.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import { getUsersApi, createUserApi, deactivateUserApi } from '../../../api/user.api';
import { USER_ROLE_LABELS } from '../../../utils/constants';

const emptyForm = { fullName: '', email: '', phone: '', password: '', role: 'OBSERVER' };

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ role: '', search: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getUsersApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setUsers(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت کاربران');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUserApi(form);
      setSnackbar('کاربر ایجاد شد');
      setFormOpen(false);
      setForm(emptyForm);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteTarget) return;
    try {
      await deactivateUserApi(deleteTarget);
      setSnackbar('کاربر غیرفعال شد');
      setDeleteTarget(null);
      loadUsers();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">مدیریت کاربران</h1>
        <Button variant="primary" onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="ml-2 h-4 w-4" /> کاربر جدید
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border border-slate-200 p-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
        >
          <option value="">همه نقش‌ ها</option>
          {Object.entries(USER_ROLE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <Input
          placeholder="جستجوی نام یا ایمیل..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="h-11 w-64"
        />
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 overflow-x-auto">
        {loading ? (
          <div className="py-8 text-center text-text-dark/50">در حال بارگذاری...</div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="bg-slate-50 text-text-dark/70">
                <tr>
                  <th className="px-4 py-3 font-medium">نام</th>
                  <th className="px-4 py-3 font-medium">ایمیل</th>
                  <th className="px-4 py-3 font-medium">نقش</th>
                  <th className="px-4 py-3 font-medium">باشگاه</th>
                  <th className="px-4 py-3 font-medium">وضعیت</th>
                  <th className="px-4 py-3 text-center font-medium">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3">{u.fullName}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{USER_ROLE_LABELS[u.role]}</td>
                    <td className="px-4 py-3">{u.club?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                        u.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {u.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.isActive && (
                        <button onClick={() => setDeleteTarget(u.id)} className="text-red-500 hover:text-red-700 text-xs font-bold">
                          غیرفعال کردن
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-text-dark/40">
                      کاربری ثبت نشده است
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="کاربر جدید">
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <Input label="نام کامل" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
          <Input label="ایمیل" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <Input label="تلفن" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="رمز عبور" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <select
            className="w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {Object.entries(USER_ROLE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>انصراف</Button>
            <Button variant="primary" type="submit">ثبت</Button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="تأیید غیرفعال‌ سازی">
        <p className="text-text-dark/70">آیا از غیرفعال‌ سازی این کاربر مطمئن هستید؟</p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)}>انصراف</Button>
          <Button variant="primary" onClick={handleDeactivate}>تأیید</Button>
        </div>
      </Modal>
    </div>
  );
}